import React from "react";
import type { RouteObject } from "react-router-dom";
import { AUTH_ROUTES } from "../constants/authScreenCopy";

/* Lazy: the shell and SSO chunks load only when an auth route opens. */
const AuthShell = React.lazy(() => import("../components/templates/AuthShell/AuthShell"));
const LoginSsoPage = React.lazy(() => import("../pages/LoginSsoPage"));

/** The page element App renders for each auth route. */
export interface AuthRoutePages {
  signIn: React.ReactElement;
  signUp: React.ReactElement;
  forgotPassword: React.ReactElement;
  resetPassword: React.ReactElement;
  verifyEmail: React.ReactElement;
  oauthCallback: React.ReactElement;
  /** Signed-in only: App passes it already wrapped in `ProtectedRoute`. */
  selectOrganization: React.ReactElement;
  /** Emailed sign-in link landing (`/login/magic`). */
  signInMagic: React.ReactElement;
}

/**
 * Auth routes: every path (plus `/login/sso` and `/login/magic`) sits under one pathless
 * AuthShell layout route, so the shell (and, from Phase 4, its carousel) stays mounted while
 * the user moves between sign-in, sign-up, verification, password reset and
 * the organization picker. The picker keeps its `ProtectedRoute`, so a
 * signed-out visit still redirects to sign-in, now inside the shell.
 */
export const buildAuthRoutes = (pages: AuthRoutePages): RouteObject[] => {
  const routes: RouteObject[] = [
    { path: AUTH_ROUTES.signIn, element: pages.signIn },
    { path: AUTH_ROUTES.signUp, element: pages.signUp },
    { path: AUTH_ROUTES.forgotPassword, element: pages.forgotPassword },
    { path: AUTH_ROUTES.resetPassword, element: pages.resetPassword },
    { path: AUTH_ROUTES.verifyEmail, element: pages.verifyEmail },
    { path: AUTH_ROUTES.oauthCallback, element: pages.oauthCallback },
    { path: AUTH_ROUTES.selectOrganization, element: pages.selectOrganization },
  ];
  return [
    {
      element: <AuthShell />,
      children: [
        ...routes,
        { path: AUTH_ROUTES.signInSso, element: <LoginSsoPage /> },
        { path: AUTH_ROUTES.signInMagic, element: pages.signInMagic },
      ],
    },
  ];
};

/** For routes outside the shell group that render sign-in (logged-out `/`). */
export const withAuthShell = (page: React.ReactElement): React.ReactElement => (
  <AuthShell>{page}</AuthShell>
);
